import '@uirouter/angularjs';
import 'angular-ui-bootstrap';
import 'angular-local-storage';
import 'angular-translate';
import 'angular-translate-handler-log';
import 'angular-dynamic-locale';
import 'angular-moment';
import 'angular-sanitize';
import 'moment-timezone';
import 'ng-toast';
import 'ui-select';
import 'oclazyload';
import 'ng-raven';
import * as angularDragula from 'angularjs-dragula';

// styles for external libraries
import 'ui-select/dist/select.min.css';
import 'ng-toast/dist/ngToast.min.css';
import 'ng-toast/dist/ngToast-animations.min.css';
import 'angularjs-dragula/dist/dragula.min.css';

import './lodashAngular';
import './d3Angular';

// import environment for debug info configuration
import environment from '../../environment';

// import primary assets
import '../../assets/images/favicon_new.ico';
import translations from './translations';
import coreServices from './services';
import coreComponents from './components';
import filters from './filters';
import models from './models';
import LoggedInUser from './LoggedInUser';

const module = angular.module('websked.core', [
  'ui.router',
  'ui.bootstrap',
  'LocalStorageModule',
  'pascalprecht.translate',
  'tmh.dynamicLocale',
  'ngSanitize',
  'lodashAngular',
  'angularMoment',
  'ngToast',
  'ui.select',
  'oc.lazyLoad',
  'd3',
  'ngRaven',
  angularDragula(angular)
]);

const toLoad = [
  coreServices,
  coreComponents,
  translations,
  models,
  filters,
  LoggedInUser
];

toLoad.forEach(fn => fn(module));

module.constant('localDev', !environment.prod);

// Disable debug info in production
module.config(['$compileProvider', 'localDev', ($compileProvider, localDev) => {
  $compileProvider.debugInfoEnabled(localDev);
}]);

function configTranslate($translateProvider, i18nDe, i18nEn, i18nEs, i18nFr,
  i18nJa, i18nKo, i18nAr, i18nPt) {
  $translateProvider.translations('de', i18nDe);
  $translateProvider.translations('en', i18nEn);
  $translateProvider.translations('es', i18nEs);
  $translateProvider.translations('fr', i18nFr);
  $translateProvider.translations('ja', i18nJa);
  $translateProvider.translations('ko', i18nKo);
  $translateProvider.translations('ar', i18nAr);
  $translateProvider.translations('pt', i18nPt);

  $translateProvider.registerAvailableLanguageKeys(
    ['de', 'en', 'es', 'fr', 'ja', 'ko', 'ar', 'pt'],
    {
      'ar*': 'ar',
      'pt*': 'pt',
      'de*': 'de',
      'en*': 'en',
      'es*': 'es',
      'fr*': 'fr',
      'ja*': 'ja',
      'ko*': 'ko',
      'nb*': 'en',
      'nn*': 'en',
      'no*': 'en',
      'sv*': 'en',
      '*': 'en'
    }
  );
  $translateProvider.fallbackLanguage('en').determinePreferredLanguage();
  $translateProvider.useSanitizeValueStrategy('escape');
  $translateProvider.useMissingTranslationHandlerLog();
}

module.config([
  '$translateProvider',
  'i18nDe',
  'i18nEn',
  'i18nEs',
  'i18nFr',
  'i18nJa',
  'i18nKo',
  'i18nAr',
  'i18nPt',
  configTranslate
]);

function configLocaleProvider(tmhDynamicLocaleProvider) {
  tmhDynamicLocaleProvider.localeLocationPattern('./locales/angular-locale_{{locale}}.js');
}

module.config([
  'tmhDynamicLocaleProvider',
  configLocaleProvider
]);

function configLocal(localStorageServiceProvider) {
  const localStorageSvc = localStorageServiceProvider;
  localStorageSvc.prefix = 'arc';
}

module.config([
  'localStorageServiceProvider',
  configLocal
]);

// http://www.webdeveasy.com/interceptors-in-angularjs-and-useful-examples/
function configHttp($httpProvider) {
  const http = $httpProvider;
  // global interceptor
  // https://docs.angularjs.org/api/ng/service/$http
  http.interceptors.push([
    '$q',
    'utilityService',
    ($q, utilityService) => ({
      responseError(rejection) {
        if (rejection.status === -1) {
          utilityService.unauthRedirect();
        }
        return $q.reject(rejection);
      }
    })]);

  $httpProvider.interceptors.push('timestampMarker');

  // TODO: Determine if this is really necessary
  // Denis added it ... should we really be setting separate headers for IE?
  if (!$httpProvider.defaults.headers.get) {
    http.defaults.headers.get = {};
  }

  // force ALL possible IE-related headers
  http.defaults.headers.get['If-Modified-Since'] = '-1';
  http.defaults.headers.get['Cache-Control'] = 'no-cache';
  http.defaults.headers.get.Pragma = 'no-cache';
}

module.config([
  '$httpProvider',
  configHttp
]);

// TODO: determine if this is really needed
module.config(['$animateProvider', ($animateProvider) => {
  $animateProvider.classNameFilter(/^((?!(repeat-dynamic)).)*$/);
}]);

function toastConfig(ngToast) {
  ngToast.configure({
    verticalPosition: 'bottom',
    horizontalPosition: 'left',
    maxNumber: 3
  });
}

module.config(['ngToastProvider', toastConfig]);

angular.element(document).ready(() => {
  angular.bootstrap(document, ['websked']);
});

function configRun(
  $log,
  $transitions,
  analyticsService,
  languageProvider,
  moment,
  permissionsService,
  timezoneService,
  platformService,
  siteService,
  tmhDynamicLocale,
  tmhDynamicLocaleCache
) {
  $transitions.onSuccess({}, () => {
    analyticsService.boot();
  });

  $transitions.onBefore({}, () => {
    const requests = [];

    // Initialize permissions
    if (!_.has(permissionsService, 'permissions')) {
      requests.push(permissionsService.init());
    }

    // Initialize Global Settings timezone config
    if (!_.has(timezoneService, 'globalSettingsConfig')) {
      requests.push(timezoneService.init());
    }

    // Initialize websites
    if (!_.has(siteService, 'cache.websites')) {
      requests.push(siteService.getWebsites());
    }

    // Initialize platforms list
    if (!_.has(platformService, 'cache.all')) {
      requests.push(platformService.getAll());
    }

    if (requests.length > 0) {
      return Promise.all(requests);
    }

    return true;
  });

  // adjust browser locale code to moment locale code
  const getMomentLocaleCode = (browserLocaleCode) => {
    // if Norwegian locale, default to Bokmål
    if (browserLocaleCode === 'no') {
      return 'nb';
    }
    return browserLocaleCode;
  };

  // Set moment locale
  moment.locale(getMomentLocaleCode(languageProvider.locale));

  // Set AngularJS locale
  tmhDynamicLocale.set(languageProvider.locale).then((data) => {
    const customData = data;
    if (languageProvider.language === 'ja') customData.DATETIME_FORMATS.fullDate = 'y年M月d日 EEEE';
    tmhDynamicLocaleCache.put(languageProvider.locale, customData);
  }).catch((error) => {
    $log.error('Error setting locale for WebSked:\n', error);
  });
}

module.run([
  '$log',
  '$transitions',
  'analyticsService',
  'languageProvider',
  'moment',
  'permissionsService',
  'timezoneService',
  'platformService',
  'siteService',
  'tmhDynamicLocale',
  'tmhDynamicLocaleCache',
  configRun
]);

module.factory('timestampMarker', [() => {
  const timestampMarker = {
    request(config) {
      const cfg = config;
      cfg.requestTimestamp = new Date().getTime();
      return cfg;
    },
    response(response) {
      response.config.responseTimestamp = new Date().getTime();
      return response;
    }
  };
  return timestampMarker;
}]);
